import React, { createContext, useCallback, useMemo, useState } from 'react'
import { merge } from 'lodash'
import UserApi from '../api/UserApi'
import useAuth from '../hooks/useAuth'
import { useInterval } from '../hooks/useInterval'
import { useGlobalLoader } from '../hooks/useGlobalLoader'
import { userMessageAtom, userMessageStore } from '../atoms/userMessageAtom'
import { PatchUserMeRequest, UserMe } from '@finance-lab/utils/types'

const userApi = new UserApi()

export const UserMeContext = createContext<{
    isLoading: boolean
    updateMe: (d: Parameters<typeof userApi.updateMe>[0]) => Promise<PatchUserMeRequest['Response'] | null>
    user: UserMe | null
}>({
    isLoading: false,
    updateMe: (data) => userApi.updateMe(data),
    user: null,
})

export const UserMeProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<UserMe | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { incrementLoader, decrementLoader } = useGlobalLoader()
    const { isAuthenticated } = useAuth()

    const fetcher = useCallback(async () => {
        const result = await userApi.getMe()

        setUser(result)
        setIsLoading(false)
    }, [])

    useInterval(fetcher, 60 * 10 * 1000, isAuthenticated)

    const updateMe = useCallback(
        async (data: PatchUserMeRequest['Body']) => {
            setUser(merge({}, user, data)) // Optimistic update with lodash.merge since data is partial
            incrementLoader()
            let result: PatchUserMeRequest['Response'] | null = null

            try {
                result = await userApi.updateMe(data)
                setUser(result)
            } catch (err) {
                console.error(err)
                userMessageStore.set(userMessageAtom, {
                    type: 'error',
                    errorCode: null,
                    message: `Ett oväntat fel inträffade, inställningen sparades inte.`,
                })
            } finally {
                decrementLoader()
            }

            return result
        },
        [decrementLoader, incrementLoader, user],
    )

    const value = useMemo(
        () => ({
            isLoading,
            updateMe,
            user,
        }),
        [isLoading, updateMe, user],
    )

    return <UserMeContext.Provider value={value}>{children}</UserMeContext.Provider>
}
